import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAccountSession } from '../../auth/AccountSessionProvider';
import { useI18n } from '../../i18n/useI18n';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import { USING_STUBS } from '../../api';
import { StubAccounts } from '../../stubs/StubBanner';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { AuthLayout } from '../../ui/AuthLayout';
import { Button } from '../../ui/primitives';
import { TextField } from '../../ui/form';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Signing in to the account, on the provider's domain, with an address and a
 * password.
 *
 * This page opens subscription work, tenancies and — for a customer's own
 * representative — the choice of identity provider. It opens nobody's hosts:
 * those are behind the identity provider each tenancy chose, on that tenancy's
 * own domain.
 *
 * Three refusals are told apart here, by code and never by sentence:
 *
 * * `bad_credentials` — the address and the password do not go together. One
 *   answer for a wrong password and for an address that names nobody, because
 *   any difference is a way to find out which addresses exist. The console does
 *   not improve on that by guessing which half was wrong;
 * * `too_many_attempts` — the window of failures is full, and it earns its own
 *   words: the right password does not pass in it either, so somebody who has
 *   just remembered theirs has to be told to wait rather than to try harder;
 * * anything else — said plainly, with nothing about the form blamed for it.
 *
 * There is no "forgot your password" here, and its absence is deliberate: this
 * product has no mail, and a reset with no second channel is a way to take an
 * account over. The screen says so rather than leaving a silence where people
 * look for the link.
 */
export function AccountLoginScreen() {
  const { t } = useI18n();
  const { signIn } = useAccountSession();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    const next: { email?: string; password?: string } = {};
    if (email.trim() === '') next.email = t('login.check.emailRequired');
    else if (!EMAIL.test(email.trim())) next.email = t('login.check.emailInvalid');
    if (password === '') next.password = t('login.check.passwordRequired');

    setFieldErrors(next);
    setFailure(null);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      await signIn({ email: email.trim(), password });
    } catch (error: unknown) {
      setFailure(toApiError(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <h1>{t('login.heading')}</h1>
      <p className="sub" style={{ marginBottom: 22 }}>
        {t('login.subtitle')}
      </p>

      {failure !== null && (
        <div style={{ marginBottom: 18 }}>
          <SignInFailure failure={failure} />
        </div>
      )}

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <TextField
          label={t('login.email.label')}
          type="email"
          name="email"
          autoComplete="username"
          placeholder={t('login.email.placeholder')}
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          {...(fieldErrors.email === undefined ? {} : { error: fieldErrors.email })}
        />
        <TextField
          label={t('login.password.label')}
          type="password"
          name="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
          {...(fieldErrors.password === undefined ? {} : { error: fieldErrors.password })}
        />
        <Button type="submit" variant="primary" block disabled={submitting}>
          {submitting ? `${t('login.submitting')}…` : t('login.submit')}
        </Button>
      </form>

      <p className="hint" style={{ marginTop: 16 }}>
        {t('login.noReset')}
      </p>

      {USING_STUBS && (
        <div style={{ marginTop: 22 }}>
          <StubAccounts />
        </div>
      )}
    </AuthLayout>
  );
}

/**
 * A full window of attempts gets its own box.
 *
 * Not "error 429": what a person needs is that the counting is per address,
 * that it passes on its own, and that the correct password does not get through
 * it in the meantime — a counter that let the right guess through would not be
 * a counter. How long the wait is comes from the answer, when the deployment
 * says so at all; when it does not, the console says what happened rather than
 * inventing a countdown.
 */
function SignInFailure({ failure }: { failure: ApiError }) {
  const { t, formatDuration } = useI18n();
  const wait = failure.retryAfterSeconds;

  if (failure.code === 'too_many_attempts') {
    return (
      <Alert tone="warn" title={t('login.tooMany.title')} live>
        <p>{t('login.tooMany.body')}</p>
        <p style={{ marginTop: 8 }}>
          {wait === undefined
            ? t('login.tooMany.unknownWait')
            : t('login.tooMany.wait', { wait: formatDuration(wait) })}
        </p>
        <p style={{ marginTop: 8 }}>{t('login.tooMany.rightPassword')}</p>
      </Alert>
    );
  }

  if (failure.code === 'bad_credentials') {
    return (
      <Alert tone="deny" title={t('login.failed')} live>
        <p>{t('login.badCredentials')}</p>
      </Alert>
    );
  }

  return <ErrorAlert error={failure} />;
}
